"use client";

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  ALL_CATEGORY,
  ALL_CITY,
  DateRangeKey,
  useFiltersStore,
} from "@/store/useFilterStore";
import { useFiltersNavigationStore } from "@/store/useFiltersNavigationStore";
import { useFiltersData } from "@/context/FiltersDataContext";
import locationService from "@/api/services/location.service";
import categoryService from "@/api/services/category.service";
// import dataService from "@/api/services/data.service";
// import { type FetchDataBody, type Period } from "@/store/useSummaryStore";

type ApiListResp<T> = { success: boolean; statusCode: number; message: string; data: T[]; };
type Option = { id: string | number; label: string };

// ------------------------------
// Utilities
// ------------------------------
const ciEq = (a?: string | number | null, b?: string | number | null) =>
  String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

function findByLabel(options: Option[] | undefined, label: string | null) {
  if (!options || !label) return undefined;
  return options.find((o) => ciEq(o.label, label));
}

function slugifyLabel(label: string) {
  return String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "");
}

function findBySlugRelaxed(options: Option[] | undefined, rawSlug?: string | null) {
  const slug = String(rawSlug ?? "").trim().toLowerCase();
  if (!options || !slug) return undefined;
  const exact = options.find((o) => slugifyLabel(o.label) === slug);
  if (exact) return exact;

  const candidates = options
    .map((o) => ({ o, oSlug: slugifyLabel(o.label) }))
    .filter(({ oSlug }) => oSlug.includes(slug) || slug.includes(oSlug))
    .sort(
      (a, b) =>
        Math.abs(a.oSlug.length - slug.length) -
        Math.abs(b.oSlug.length - slug.length)
    );

  return candidates[0]?.o;
}

function isSlugEquivalent(label?: string | null, rawSlug?: string | null) {
  if (!label || !rawSlug) return false;
  const slug = String(rawSlug).trim().toLowerCase();
  if (!slug) return false;
  const labelSlug = slugifyLabel(label);
  return (
    labelSlug === slug ||
    labelSlug.includes(slug) ||
    slug.includes(labelSlug)
  );
}

function normalizeAllToken(value?: string | null) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, " ");
}

function isAllStateValue(value?: string | null) {
  const normalized = normalizeAllToken(value);
  return normalized === "all" || normalized === "all states";
}

function isAllCityValue(value?: string | null) {
  const normalized = normalizeAllToken(value);
  return normalized === "all" || normalized === "all cities";
}

function isAllCategoryValue(value?: string | null) {
  const normalized = normalizeAllToken(value);
  return (
    normalized === "all" ||
    normalized === "all categories" ||
    normalized === "0"
  );
}

// Map any UI date-range keys to API Period
// function toPeriod(key?: string): Period {
//   switch (key) {
//     case "LAST_MONTH": return "LAST_MONTH";
//     case "LAST_3_MONTHS": return "LAST_QUARTER";
//     case "LAST_6_MONTHS": return "LAST_HALF_YEAR";
//     case "LAST_QUARTER": return "LAST_QUARTER";
//     case "LAST_HALF_YEAR": return "LAST_HALF_YEAR";
//     default: return "LAST_MONTH";
//   }
// }

function findNewDelhi(states: Option[]) {
  return (
    states?.find(s => s.label.toLowerCase().includes("new delhi")) ||
    states?.find(s => s.label.toLowerCase().includes("delhi")) ||
    states[0] || null
  );
}

// ------------------------------
// Queries for filters
// ------------------------------
export function useStatesQuery() {
  const { states: initialStates } = useFiltersData();
  const q = useQuery({
    queryKey: ["states"],
    queryFn: locationService.getState,
    staleTime: 5 * 60 * 1000,
    select: (resp: ApiListResp<Option>) => resp.data,
    initialData: initialStates
      ? {
          success: true,
          statusCode: 200,
          message: "prefetched",
          data: initialStates,
        }
      : undefined,
    initialDataUpdatedAt: initialStates ? Date.now() : undefined,
  });

  const { selectedState, setState } = useFiltersStore();
  const searchParams = useSearchParams();
  const pathname = usePathname() || "/";
  const stateParam = searchParams?.get("state");
  const pathParts = pathname.split("/").filter(Boolean);
  const knownBases = new Set([
    "order-trends",
    "rto-trends",
    "geo-trends",
    "category-trends",
    "buyer-stats",
  ]);
  const filterParts =
    pathParts.length > 0 && knownBases.has(pathParts[0])
      ? pathParts.slice(1)
      : pathParts;
  const hasCompactFilters =
    filterParts.length >= 4 &&
    !filterParts.slice(0, 4).some(
      (seg) =>
        seg.startsWith("state-") ||
        seg.startsWith("city-") ||
        seg.startsWith("category-") ||
        seg.startsWith("time-")
    );
  const hasFilterSegment = hasCompactFilters;

  useEffect(() => {
    // Only set default state if query succeeded, store empty/placeholder, and no URL param
    const hasRealState = !!selectedState && !!selectedState.id;
    if (q.isSuccess && !hasRealState && !stateParam && !hasFilterSegment) {
      const nd = findNewDelhi(q.data);
      if (nd) setState(nd as any);
    }
  }, [q.isSuccess, q.data, selectedState, setState, stateParam, hasFilterSegment]);

  return q;
}

export function useCitiesQuery(stateIdOverride?: string | null) {
  const { selectedState } = useFiltersStore();
  const { citiesByStateId } = useFiltersData();

  // If a stateId is passed, use that; otherwise fall back to store
  const stateId = stateIdOverride ?? selectedState?.id ?? null;
  const initialCities =
    stateId != null ? citiesByStateId?.[String(stateId)] : undefined;

  return useQuery({
    queryKey: ["cities", stateId],
    queryFn: () => locationService.getCity(stateId as string),
    enabled: !!stateId && !initialCities, // skip fetch when server provided cities
    staleTime: 60_000,
    select: (resp: ApiListResp<Option>) => [ALL_CITY, ...resp.data],
    initialData: initialCities
      ? {
          success: true,
          statusCode: 200,
          message: "prefetched",
          data: initialCities,
        }
      : undefined,
    initialDataUpdatedAt: initialCities ? Date.now() : undefined,
  });
}

export function useCategoriesQuery() {
  const { categories: initialCategories } = useFiltersData();
  return useQuery({
    queryKey: ["categories"],
    queryFn: categoryService.getcategories,
    staleTime: 10 * 60 * 1000,
    select: (resp: ApiListResp<Option>) => [ALL_CATEGORY, ...resp.data],
    initialData: initialCategories
      ? {
          success: true,
          statusCode: 200,
          message: "prefetched",
          data: initialCategories,
        }
      : undefined,
    initialDataUpdatedAt: initialCategories ? Date.now() : undefined,
  });
}

// ------------------------------
// Debounce helper
// ------------------------------
// function useDebounced<T>(value: T, ms = 300): T {
//   const [v, setV] = useState(value);
//   useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
//   return v;
// }

// ------------------------------
// Summary query (client version disabled; server-only now)
// ------------------------------

// ------------------------------
// URL <-> Store sync using LABELS for state & city
// state=Delhi  city=New%20Delhi  catId=42  pType=LAST_QUARTER
// ------------------------------
// ----------------------------
// Filters URL <-> Store sync
// ----------------------------
export const useFiltersUrlSync = () => {
  const router = useRouter();
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();

  const {
    selectedState,
    selectedCity,
    selectedCategory,
    selectedDateRange,
    setState,
    setCity,
    setCategory,
    setDateRange,
  } = useFiltersStore();
  const setSyncing = useFiltersNavigationStore((s) => s.setSyncing);
  const isSyncing = useFiltersNavigationStore((s) => s.isSyncing);

  const { data: states } = useStatesQuery();
  const { data: categories } = useCategoriesQuery();
  const { data: cities } = useCitiesQuery();
  const { initialFilters } = useFiltersData();

  const hydrated = useRef(false);
  const cityHydrated = useRef(false);

  const rawStateParam = searchParams?.get("state");
  const stateParam =
    rawStateParam && !isAllStateValue(rawStateParam) ? rawStateParam : null;
  const rawCityParam = searchParams?.get("city");
  const cityParam =
    rawCityParam && !isAllCityValue(rawCityParam) ? rawCityParam : null;
  const rawCatParam =
    searchParams?.get("catId") ??
    searchParams?.get("cat") ??
    searchParams?.get("category");
  const catParam =
    rawCatParam && !isAllCategoryValue(rawCatParam) ? rawCatParam : null;
  const periodParam = searchParams?.get("pType") ?? searchParams?.get("period");

  // ----------------------------
  // Parse path-style filters
  // e.g. /state-delhi/city-newdelhi/category-all/time-last6months
  // or /order-trends/state-delhi/...
  // ----------------------------
  const segments = pathname.split("/").filter(Boolean);
  const knownBases = new Set([
    "order-trends",
    "rto-trends",
    "geo-trends",
    "category-trends",
    "buyer-stats",
  ]);

  const baseSlug = segments.length > 0 && knownBases.has(segments[0])
    ? segments[0]
    : null;
  const filterSegments =
    baseSlug != null ? segments.slice(1) : segments;
  const currentBase = baseSlug ?? "dashboard";
  const hasLegacyPrefixedFilterSegments = filterSegments.some(
    (seg) =>
      seg.startsWith("state-") ||
      seg.startsWith("city-") ||
      seg.startsWith("category-") ||
      seg.startsWith("time-")
  );

  let stateSlug: string | null = null;
  let citySlug: string | null = null;
  let categoryPart: string | null = null;
  let timeSlug: string | null = null;

  // Only compact format is supported: /<state>/<city>/<category>/<time>
  if (filterSegments.length >= 4) {
    const [s, c, cat, t] = filterSegments;
    const hasLegacyPrefixes = [s, c, cat, t].some(
      (seg) =>
        seg?.startsWith("state-") ||
        seg?.startsWith("city-") ||
        seg?.startsWith("category-") ||
        seg?.startsWith("time-")
    );
    if (!hasLegacyPrefixes) {
      stateSlug = s || null;
      citySlug = c || null;
      categoryPart = cat || null;
      timeSlug = t || null;
    }
  }

  // `all` is only meaningful for geo-trends. If we land on other pages with
  // geo-trends-style state value in the URL, treat it as
  // "no state provided" so we can fall back to the default state.
  if (baseSlug !== "geo-trends" && stateSlug === "all") {
    stateSlug = null;
  }

  const allowedPeriods = new Set([
    "LAST_MONTH",
    "LAST_QUARTER",
    "LAST_HALF_YEAR",
    // legacy URL values (kept for compatibility)
    "LAST_3_MONTHS",
    "LAST_6_MONTHS",
  ]);

  const hasInitialFilters =
    !!initialFilters &&
    (initialFilters.state ||
      initialFilters.city ||
      initialFilters.catId != null ||
      initialFilters.pType);

  // Track first sync to avoid client-side redirect flicker on plain "/"
  const firstSync = useRef(true);

  // If user navigated away from geo-trends, reset the state filter back to default.
  // We persist the previous base in sessionStorage on unmount, since this hook is
  // mounted per page and can't rely on in-memory "previous route" state.
  const pendingGeoExitResetRef = useRef(false);

  useEffect(() => {
    try {
      const prevBase = globalThis.sessionStorage?.getItem("sr:prevBase");
      if (prevBase === "geo-trends" && currentBase !== "geo-trends") {
        pendingGeoExitResetRef.current = true;
      }
      globalThis.sessionStorage?.removeItem("sr:prevBase");
    } catch {
      // ignore
    }
  }, [currentBase]);

  useEffect(() => {
    return () => {
      try {
        globalThis.sessionStorage?.setItem("sr:prevBase", currentBase);
      } catch {
        // ignore
      }
    };
  }, [currentBase]);

  useEffect(() => {
    if (!pendingGeoExitResetRef.current) return;
    if (!states || states.length === 0) return;

    const hasExplicitState =
      !!stateParam || (stateSlug != null && stateSlug !== "all");
    if (!hasExplicitState) {
      const nd = findNewDelhi(states);
      if (nd) setState(nd as any);
    }
    pendingGeoExitResetRef.current = false;
  }, [states, setState, stateParam, stateSlug]);

  // 1️⃣ Hydrate store from URL (except city)
  useEffect(() => {
    if (hydrated.current) return;
    if (!states || !categories) return;

    // ----- State -----
    const hasStateInStore = !!selectedState && !!selectedState.id;
    if (hasInitialFilters) {
      if (initialFilters?.state) {
        const st = findByLabel(states, initialFilters.state);
        if (st && (!selectedState || String(selectedState.id) !== String(st.id))) {
          setState(st as any);
        }
      } else if (!hasStateInStore) {
        const nd = findNewDelhi(states);
        if (nd) setState(nd as any);
      }
    } else {
    if (stateSlug) {
      const st = findBySlugRelaxed(states, stateSlug);
      if (st && (!selectedState || String(selectedState.id) !== String(st.id))) {
        setState(st as any);
      }
      } else if (stateParam) {
        const st = findByLabel(states, stateParam);
        if (st && (!selectedState || String(selectedState.id) !== String(st.id))) {
          setState(st as any);
        }
      } else if (!hasStateInStore) {
        const nd = findNewDelhi(states);
        if (nd) setState(nd as any);
      }
    }

    // ----- Category -----
    const currentCatId = selectedCategory?.id ?? null;
    if (hasInitialFilters) {
      if (initialFilters?.catId != null) {
        const cat = categories.find(
          (c) => String(c.id) === String(initialFilters.catId)
        );
        if (cat && String(cat.id) !== String(currentCatId)) {
          setCategory(cat as any);
        }
      } else if (currentCatId !== ALL_CATEGORY.id) {
        setCategory(ALL_CATEGORY as any);
      }
    } else {
      if (categoryPart) {
        if (categoryPart === "all") {
          if (currentCatId !== ALL_CATEGORY.id) {
            setCategory(ALL_CATEGORY as any);
          }
        } else {
          const cat =
            categories.find(
              (c) => String(c.id).toLowerCase() === categoryPart
            ) || findBySlugRelaxed(categories, categoryPart);
          if (cat && String(cat.id) !== String(currentCatId)) {
            setCategory(cat as any);
          }
        }
      } else if (catParam) {
        const cat = categories.find((c) => String(c.id) === catParam);
        if (cat && String(cat.id) !== String(currentCatId)) {
          setCategory(cat as any);
        }
      }
    }

    // ----- Period / time -----
    if (hasInitialFilters) {
      const initialPeriod = initialFilters?.pType ?? null;
      if (initialPeriod && allowedPeriods.has(initialPeriod)) {
        if (selectedDateRange !== initialPeriod) {
          setDateRange(initialPeriod as DateRangeKey);
        }
      }
    } else {
      if (timeSlug) {
        let key: string | null = null;
        switch (timeSlug) {
          case "lastmonth":
          case "lastmonth":
            key = "LAST_MONTH";
            break;
          case "last3months":
            // legacy alias – normalize to LAST_QUARTER
            key = "LAST_QUARTER";
            break;
          case "last6months":
            // legacy alias – normalize to LAST_HALF_YEAR
            key = "LAST_HALF_YEAR";
            break;
          default:
            key = null;
        }
        if (key && allowedPeriods.has(key)) {
          if (selectedDateRange !== key) {
            setDateRange(key as DateRangeKey);
          }
        }
      } else if (periodParam && allowedPeriods.has(periodParam)) {
        // Normalize legacy query params to canonical keys used by the store
        let normalized: string = periodParam;
        if (periodParam === "LAST_3_MONTHS") {
          normalized = "LAST_QUARTER";
        } else if (periodParam === "LAST_6_MONTHS") {
          normalized = "LAST_HALF_YEAR";
        }
        if (selectedDateRange !== normalized) {
          setDateRange(normalized as DateRangeKey);
        }
      }
    }

    hydrated.current = true;
  }, [
    states,
    categories,
    initialFilters,
    hasInitialFilters,
    selectedState,
    selectedCategory,
    selectedDateRange,
    setState,
    setCategory,
    setDateRange,
    stateParam,
    catParam,
    periodParam,
    stateSlug,
    categoryPart,
    timeSlug,
  ]);

  // 2️⃣ Hydrate city after cities loaded
  useEffect(() => {
    if (!hydrated.current) return;
    if (cityHydrated.current) return;
    if (!cities) return;

    let cityToSet: Option = ALL_CITY as any;
    if (hasInitialFilters) {
      if (initialFilters?.city) {
        const c = findByLabel(cities, initialFilters.city);
        if (c) cityToSet = c as any;
      }
    } else {
      if (citySlug) {
        if (citySlug === "all") {
          cityToSet = ALL_CITY as any;
        } else {
          const c = findBySlugRelaxed(cities, citySlug);
          if (c) cityToSet = c as any;
        }
      } else if (cityParam) {
        const c = findByLabel(cities, cityParam);
        if (c) cityToSet = c as any;
      }
    }

    const currentCityId = selectedCity?.id ?? null;
    if (String(currentCityId) !== String(cityToSet?.id)) {
      setCity(cityToSet as any);
    }
    cityHydrated.current = true;
  }, [
    cities,
    cityParam,
    citySlug,
    selectedCity,
    setCity,
    hasInitialFilters,
    initialFilters,
  ]);

  // 3️⃣ Push store -> URL (path segments; preserve non-filter query params)
  useEffect(() => {
    if (!hydrated.current) return;
    if (!cityHydrated.current) return;

    // Only push URL when a user-driven filter change is in progress.
    if (!isSyncing) {
      if (firstSync.current) {
        firstSync.current = false;
      }
      return;
    }

     // On very first sync, if user landed on plain "/" with no explicit filters,
     // avoid immediately rewriting the URL to a filtered path to prevent flicker.
    const isInitialSync = firstSync.current;
    if (isInitialSync) {
      const isPlainRootNoFilters =
        segments.length === 0 &&
        !stateSlug &&
        !citySlug &&
        !categoryPart &&
        !timeSlug &&
        !stateParam &&
        !cityParam &&
        !catParam &&
        !periodParam;
      if (isPlainRootNoFilters) {
        firstSync.current = false;
        setSyncing(false);
        return;
      }
    }

    const currentSegments = pathname.split("/").filter(Boolean);
    const currentBase =
      currentSegments.length > 0 && knownBases.has(currentSegments[0])
        ? currentSegments[0]
        : null;
    const isGeoTrendsBase = currentBase === "geo-trends";

    let timeKey: string | null = null;
    switch (selectedDateRange) {
      case "LAST_MONTH":
        timeKey = "lastmonth";
        break;
      case "LAST_QUARTER":
        timeKey = "last3months";
        break;
      case "LAST_HALF_YEAR":
        timeKey = "last6months";
        break;
      default:
        timeKey = null;
    }

    const urlHasAllSegments =
      !!stateSlug && !!citySlug && !!categoryPart && !!timeSlug;
    const matchesState = isGeoTrendsBase
      ? stateSlug === "all"
      : isSlugEquivalent(selectedState?.label, stateSlug);
    const matchesCity = isGeoTrendsBase
      ? citySlug === "all"
      : !selectedCity || selectedCity.id === ALL_CITY.id
      ? citySlug === "all"
      : isSlugEquivalent(selectedCity.label, citySlug);
    const matchesCategory =
      !selectedCategory || selectedCategory.id === ALL_CATEGORY.id
        ? categoryPart === "all"
        : categoryPart === String(selectedCategory.id).toLowerCase() ||
          isSlugEquivalent(selectedCategory.label, categoryPart);
    const matchesTime = (() => {
      if (!timeSlug) return false;
      if (selectedDateRange === "LAST_MONTH") {
        return timeSlug === "lastmonth" || timeSlug === "lastmonth";
      }
      if (selectedDateRange === "LAST_QUARTER") {
        return timeSlug === "last3months";
      }
      if (selectedDateRange === "LAST_HALF_YEAR") {
        return timeSlug === "last6months";
      }
      return false;
    })();

    if (
      urlHasAllSegments &&
      matchesState &&
      matchesCity &&
      matchesCategory &&
      matchesTime &&
      !hasLegacyPrefixedFilterSegments
    ) {
      setSyncing(false);
      if (isInitialSync) {
        firstSync.current = false;
      }
      return;
    }

    const parts: string[] = [];

    if (isGeoTrendsBase) {
      parts.push("all", "all");
    } else {
      if (selectedState?.label) {
        parts.push(slugifyLabel(selectedState.label));
      }

      if (!selectedCity || selectedCity.id === ALL_CITY.id) {
        parts.push("all");
      } else if (selectedCity.label) {
        parts.push(slugifyLabel(selectedCity.label));
      }
    }

    if (!selectedCategory || selectedCategory.id === ALL_CATEGORY.id) {
      parts.push("all");
    } else if (selectedCategory.label) {
      parts.push(slugifyLabel(selectedCategory.label));
    } else if (selectedCategory.id) {
      // fallback for missing label
      parts.push(String(selectedCategory.id).toLowerCase());
    }

    if (timeKey) {
      parts.push(timeKey);
    }
    const targetPath =
      "/" +
      [currentBase ?? undefined, ...parts].filter(Boolean).join("/");

    const query = new URLSearchParams(searchParams?.toString());
    const filterKeys = [
      "state",
      "city",
      "catId",
      "cat",
      "category",
      "pType",
      "period",
    ];
    filterKeys.forEach((key) => query.delete(key));

    const queryString = query.toString();
    const targetUrl = queryString ? `${targetPath}?${queryString}` : targetPath;
    const currentQuery = searchParams?.toString() ?? "";
    const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;

    if (targetUrl !== currentUrl) {
      if (!isInitialSync) {
        setSyncing(true);
      }
      router.replace(targetUrl, { scroll: false });
    } else {
      setSyncing(false);
    }
    if (isInitialSync) {
      firstSync.current = false;
    }
  }, [
    selectedState?.label,
    selectedCity?.id,
    selectedCity?.label,
    selectedCategory?.id,
    selectedDateRange,
    pathname,
    router,
    searchParams,
    hasLegacyPrefixedFilterSegments,
    setSyncing,
    isSyncing,
    hydrated.current,
    cityHydrated.current,
  ]);
};
